import { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { Box } from '@99designs/emu';
import { colors, maxWidths, spaces } from '@99designs/emu/settings';

interface StickyBarProps {
    children: React.ReactNode;
    onStickyTriggered: (isSticky: boolean) => void;
}

export const STICKY_BAR_HEIGHT_PX = 80;

const Outer = styled(Box)<{ sticky: boolean; stickyHeight: number }>`
    ${({ sticky, stickyHeight }) =>
        sticky &&
        css`
            height: ${stickyHeight}px;
        `}
`;

const Container = styled(Box)<{ sticky: boolean }>`
    ${({ sticky }) =>
        sticky &&
        css`
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            display: flex;
            width: 100%;
            height: ${STICKY_BAR_HEIGHT_PX}px;
            background: ${colors.white};
            box-shadow: 0px 5px 5px rgba(0, 0, 0, 0.05);
            transition: box-shadow 0.3s ease-in;
            z-index: 10;
        `}
`;

const Inner = styled(Box)<{ sticky: boolean }>`
    ${({ sticky }) =>
        sticky &&
        css`
            display: flex;
            width: 100%;
            align-items: center;
            margin: 0 auto;
            padding-left: ${spaces['2bu']};
            padding-right: ${spaces['2bu']};

            .max--widescreen & {
                max-width: ${maxWidths.content['widescreen']}px;
            }
            .max--desktop & {
                max-width: ${maxWidths.content['desktop']}px;
            }
        `}
`;

export function StickyBar({ onStickyTriggered, children }: StickyBarProps) {
    const [barHeight, setBarHeight] = useState(0);
    const [isSticky, setSticky] = useState(false);

    const outerRef = useRef<HTMLDivElement>(null);
    const innerRef = useRef<HTMLDivElement>(null);

    const offsetY = (STICKY_BAR_HEIGHT_PX - barHeight) / 2;

    useEffect(() => {
        onStickyTriggered(isSticky);
    }, [isSticky, onStickyTriggered]);

    useEffect(() => {
        if (innerRef.current) {
            setBarHeight(innerRef.current.offsetHeight);
        }
    }, []);

    useEffect(() => {
        function handleScroll() {
            window.requestAnimationFrame(() => {
                if (!innerRef.current || !outerRef.current) {
                    return;
                }

                const isOnTop = innerRef.current.getBoundingClientRect().top - offsetY <= 0;

                if (isOnTop && !isSticky) {
                    setSticky(true);
                } else if (
                    window.pageYOffset <
                    outerRef.current.getBoundingClientRect().top - offsetY + window.pageYOffset
                ) {
                    setSticky(false);
                }
            });
        }

        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [isSticky, offsetY]);

    return (
        <Outer stickyHeight={barHeight} sticky={isSticky} ref={outerRef}>
            <Container sticky={isSticky}>
                <Inner ref={innerRef} sticky={isSticky}>
                    {children}
                </Inner>
            </Container>
        </Outer>
    );
}
